import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { OAuthErrorEvent, OAuthService } from 'angular-oauth2-oidc';
import { BehaviorSubject, Observable, combineLatest, firstValueFrom } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { ApplicationAPIService } from 'src/app/extensions/services/api/application-api.service';
import { GlobalService } from 'src/app/extensions/services/global.service';
import { ENV_CONFIG } from '../injection.token';
import { isValidGuid } from 'src/app/extensions/utilities';
import { TenantAPIService } from 'src/app/extensions/services/api/tenant-api.service';
import { ROUTES } from 'src/app/core/constants/routes-constants';
import { RETURN_URL_KEY } from 'src/app/extensions/constants/commonConstants';
import { StartupService } from './startup.service';

@Injectable({ providedIn: 'root' })
export class AuthorizeService {
  private isAuthenticatedSubject$ = new BehaviorSubject<boolean>(false);
  public isAuthenticated$ = this.isAuthenticatedSubject$.asObservable();

  private isDoneLoadingSubject$ = new BehaviorSubject<boolean>(false);
  public isDoneLoading$ = this.isDoneLoadingSubject$.asObservable();

  public canActivateProtectedRoutes$: Observable<boolean> = combineLatest([
    this.isAuthenticated$,
    this.isDoneLoading$,
  ]).pipe(map(values => values.every(b => b)));

  private navigateToLoginPage(returnUrl: string | undefined = undefined) {
    this._router.navigateByUrl('/' + ROUTES.SHOULD_LOGIN_ROUTE_URL);
    if (returnUrl && returnUrl !== '/') {
      localStorage.setItem(RETURN_URL_KEY, returnUrl);
    }
  }

  constructor(
    @Inject(ENV_CONFIG) private _config: env.EnvData,
    private _oauthService: OAuthService,
    private _router: Router,
    private _globalService: GlobalService,
    private _startupService: StartupService,
    private _applicationAPIService: ApplicationAPIService
  ) {
    if (this._config.LOGGING_ENABLED) {
      this._oauthService.events.subscribe(event => {
        if (event instanceof OAuthErrorEvent) {
          console.error('OAuthErrorEvent Object:', event);
        } else {
          console.warn('OAuthEvent Object:', event);
        }
      });
    }

    window.addEventListener('storage', event => {
      if (event.key !== 'access_token' && event.key !== null) {
        return;
      }

      if (this._config.LOGGING_ENABLED) {
        console.warn('Noticed changes to access_token (most likely from another tab), updating isAuthenticated');
      }
      this.isAuthenticatedSubject$.next(this._oauthService.hasValidAccessToken());

      if (!this._oauthService.hasValidAccessToken()) {
        this.navigateToLoginPage();
      }
    });

    this._oauthService.events.subscribe(_ => {
      this.isAuthenticatedSubject$.next(this._oauthService.hasValidAccessToken());
    });
    this.isAuthenticatedSubject$.next(this._oauthService.hasValidAccessToken());

    this._oauthService.events.pipe(filter(e => ['token_received'].includes(e.type))).subscribe(e => {
      if (this._config.LOGGING_ENABLED) {
        console.log('Authenticated');
      }
    });

    this._oauthService.events
      .pipe(filter(e => ['session_terminated', 'session_error'].includes(e.type)))
      .subscribe(e => this.navigateToLoginPage());

    this._oauthService.setupAutomaticSilentRefresh();
  }

  public runInitialLoginSequence(): Promise<void> {
    if (location.hash) {
      if (this._config.LOGGING_ENABLED) {
        console.log('Encountered hash fragment, plotting as table...');
        console.table(
          location.hash
            .substring(1)
            .split('&')
            .map(kvp => kvp.split('='))
        );
      }
    }

    return this._oauthService
      .loadDiscoveryDocument()

      .then(() => new Promise<void>(resolve => setTimeout(() => resolve(), 1500)))

      .then(() => this._oauthService.tryLogin())

      .then(async () => {
        if (this._oauthService.hasValidAccessToken()) {
          await this._startupService.init();

          return Promise.resolve();
        }

        return this._oauthService
          .silentRefresh()
          .then(info => {
            if (this._config.LOGGING_ENABLED) {
              console.warn('Silent refresh success:', info);
            }
            return Promise.resolve();
          })
          .catch(result => {
            const errorResponsesRequiringUserInteraction = [
              'interaction_required',
              'login_required',
              'account_selection_required',
              'consent_required',
            ];

            if (this._config.LOGGING_ENABLED) {
              console.error('Silent refresh error:', result);
            }

            if (result?.reason && errorResponsesRequiringUserInteraction.indexOf(result.reason.error) >= 0) {
              if (this._config.LOGGING_ENABLED) {
                console.warn('User interaction is needed to log in, we will wait for the user to manually log in.');
              }
              return Promise.resolve();
            }

            // We can't handle the truth, just pass on the problem to the
            // next handler.
            return Promise.reject(result);
          });
      })

      .then(() => {
        this.isDoneLoadingSubject$.next(true);

        // Check for the strings 'undefined' and 'null' just to be sure. Our current
        // login(...) should never have this, but in case someone ever calls
        // initImplicitFlow(undefined | null) this could happen.
        if (
          this._oauthService.state &&
          this._oauthService.state !== 'undefined' &&
          this._oauthService.state !== 'null'
        ) {
          let stateUrl = this._oauthService.state;
          if (stateUrl.startsWith('/') === false) {
            stateUrl = decodeURIComponent(stateUrl);
          }
          this._router.navigateByUrl(stateUrl);
        }
      })
      .catch(error => {
        if (this._config.LOGGING_ENABLED) {
          console.log('runInitialLoginSequence error:', error);
        }
        this.isDoneLoadingSubject$.next(true);
      });
  }

  public login(targetUrl?: string, forcedLogin: boolean = false) {
    if (forcedLogin) {
      this.navigateToLoginPage(targetUrl);
      return;
    }

    if (!(this._globalService.appId == null || !isValidGuid(this._globalService.appId))) {
      this._oauthService.initLoginFlow(targetUrl ?? this._router.url, {
        'ui-culture': this._globalService.language,
        'ui-culture-timestamp': this._globalService.languageTimestamp,
        appId: this._globalService.appId,
      });
      return;
    }

    this._applicationAPIService.GetAppId(this._config.APP_CODE).subscribe(value => {
      this._globalService.appId = value;

      this._oauthService.initLoginFlow(targetUrl ?? this._router.url, {
        'ui-culture': this._globalService.language,
        'ui-culture-timestamp': this._globalService.languageTimestamp,
        appId: this._globalService.appId,
      });
    });
  }

  public logout() {
    localStorage.removeItem('user');
    this._oauthService.logOut();
  }

  public silentRefresh() {
    this._oauthService.silentRefresh();
  }

  public refreshToken() {
    this._oauthService.refreshToken();
  }

  public hasValidToken() {
    return this._oauthService.hasValidAccessToken();
  }

  public get accessToken() {
    return this._oauthService.getAccessToken();
  }

  public get identityClaims() {
    return this._oauthService.getIdentityClaims();
  }

  public get idToken() {
    return this._oauthService.getIdToken();
  }

  public get logoutUrl() {
    return this._oauthService.logoutUrl;
  }
}
